import { setAlert } from './alertActions';
import mixpanel from 'mixpanel-browser';

import { db, auth } from '../config/firebase-config';
import { collection, query, where, getDocs, getDoc, addDoc, updateDoc, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signInWithEmailLink, updateEmail, updatePassword, updateProfile, onAuthStateChanged, sendPasswordResetEmail, confirmPasswordReset, signOut } from 'firebase/auth';
import { getPremiumStatus } from '../config/getPremiumStatus';

import { 
    REGISTER_SUCCESS, 
    REGISTER_FAIL, 
    USER_LOADED,
    ACCOUNT_LOADING,
    ACCOUNT_LOADING_DONE,
    GET_USER,
    UPDATE_AUTH, 
    AUTH_ERROR, 
    USER_ERROR, 
    LOGIN_SUCCESS, 
    LOGIN_FAIL, 
    LOGOUT,
    CLEAR_USER,
    SET_STATUS
} from './types';

// Reference to the "users" collection in Firestore
const usersCollectionRef = collection(db, "users");

const handle_create_user = async (
    userCredential, 
    {
        email,
        verified
    },
) => {

    // Store the user ID in local storage
    localStorage.setItem("user_id", userCredential.user.uid);

    console.log('Creating User');

    if(verified === null || verified === undefined) {
        verified = false;
    }

    // Create a new user document in the users collection
    setDoc(doc(db, "users", userCredential.user.uid), {
        email: email.toLowerCase(),
        default_language: 'Python',
        problem_style: 'leetcode',
        verified,
        _id: userCredential.user.uid,
        date: Date.now(),
        createdAt: serverTimestamp(),
        lastModified: serverTimestamp()
    })
        .then((result) => {
            console.log('Created User');

            // ---- SEND TO MIXPANEL ----

            // Check if in production environment
            if (process.env.NODE_ENV === 'production') {

                // Initialize Mixpanel with provided ID and enable debug mode
                mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN, {
                    debug: true
                });
    
                // Assign an alias to the user
                mixpanel.alias(userCredential.user.uid);
    
                // Track the event "Complete Registration" and provide event properties
                mixpanel.track("Complete Registration", {
                    "Email": email.toLowerCase(),
                    "Registration Date": new Date().toISOString(),
                    "Registration Method": "Email/Password"
                });

                // Set people properties in Mixpanel
                mixpanel.people.set({
                    "$email": email.toLowerCase(),
                    "Registration Date": new Date().toISOString(),
                    "Registration Method": "Email/Password"
                })
            }

            // ---- END: SEND TO MIXPANEL ----

            setAccountLoadingDone();
            
            // Redirect after the user created
            window.location.href = "/";

        });

}

// Load User
export const loadUser = (register, login) => async dispatch => {

    // Log a message indicating that the user is being loaded
    console.log('LOADING USER HERE!!')

    try {

        // Check if the user ID is stored in localStorage
        if(localStorage.user_id) {

            // Log the user ID from localStorage
    
            // Get a reference to the user document in the database
            const docRef = doc(db, 'users', localStorage.user_id)
    
            // Retrieve the user document from the database
            const userDoc = await getDoc(docRef);

            // Log the user data retrieved from the database
            console.log('LOAD USER');

             // Log the user ID for Mixpanel tracking

            // Dispatch the USER_LOADED action with the user data
            dispatch({
                type: USER_LOADED,
                payload: userDoc.data()
            });

            // -- CHECK PREMIUM STATUS --
            const isPremium = await getPremiumStatus();
            dispatch(setIsPremium(isPremium));

            // ---- SEND TO MIXPANEL ----

            // Check if in production environment
            if (process.env.NODE_ENV === 'production') {

                // Initialize Mixpanel with provided ID and enable debug mode
                mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN, {
                    debug: true
                });

                // -- Opt out specific user accounts from tracking ---

                // Opt out specific users from tracking
                const optOutEmails = [
                    'tunde262@gmail.com',
                ];

                // dont track actions from opted out accounts
                if (optOutEmails.includes(userDoc.data().email.toLowerCase())) {
                    mixpanel.opt_out_tracking();
                    console.log(`User ${userDoc.data().email} has been opted out of tracking.`);
                }

                // -- END: Opt out specific user accounts from tracking ---
    
                // If it's a registration and not a login
                if(register && !login) {

                    // Assign an alias to the user
                    mixpanel.alias(userDoc.data()._id);
    
                    // Track the event "Complete Registration" and provide event properties
                    mixpanel.track("Complete Registration", {
                        "Email": userDoc.data().email,
                        "Registration Date": new Date().toISOString(),
                        "Registration Method": "Email/Password"
                    });
    
                    // Set people properties in Mixpanel
                    mixpanel.people.set({
                        "$email": userDoc.data().email,
                        "Registration Date": new Date().toISOString(),
                        "Registration Method": "Email/Password"
                    })
                } 
    
                // If it's a login and not a registration
                if(!register && login) {

                    // Identify the user
                    mixpanel.identify(userDoc.data()._id);
    
                    // Track the event "Login" and provide event properties
                    mixpanel.track("Login", {
                        "Email": userDoc.data().email,
                        "Login Method": "Sign in link",
                        "Login Date": new Date().toISOString(),
                    });
    
                    // Update people properties in Mixpanel
                    mixpanel.people.set({
                        "Last Login Date": new Date().toISOString()
                    });
                } 
            }

            // ---- END: SEND TO MIXPANEL ----
            
        } else {

            // If localStorage doesn't have 'user_id', dispatch AUTH_ERROR action
            dispatch({
                type: AUTH_ERROR
            })
        }
    } catch {

        // If an error occurs, dispatch AUTH_ERROR action
        dispatch({
            type: AUTH_ERROR
        })
    }
}

// Get single User by id
export const getUserById = id => async dispatch => {

    // Dispatch the clearUser action to reset the user state
    dispatch(clearUser());
    try {
        const docRef = doc(db, 'users', id)

        // Retrieve the document with the specified id from the 'users' collection
        const userDoc = await getDoc(docRef);

        console.log('GOT USER BY ID');
  
        // Dispatch the GET_USER action with the user document data as the payload
        dispatch({
            type: GET_USER,
            payload: userDoc.data()
        });
    } catch (err) {

        // If an error occurs, dispatch the USER_ERROR action with an error message and status code as the payload
        dispatch({
            type: USER_ERROR,
            payload: { msg: "something went wrong", status: 500 }
        });
        console.log(err);
    }
}

// Update auth inputs
export const updateAuth = (formObj) => dispatch => {
    
    // Dispatch the UPDATE_AUTH action with the provided form object as the payload
    dispatch({
        type: UPDATE_AUTH,
        payload: formObj
    });
}

// Register User w/ Password 
export const registerWithEmailAndPassword = (
    email,
    password
) => async dispatch => {

    dispatch(setAccountLoading());

    console.log('START REGISTER HERE');

    try {

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        handle_create_user(
            userCredential, 
            {
                email
            },
        )
        
    } catch (err) {
        
        // --- Handle different error codes and dispatch corresponding actions or alerts
        if(err.code == 'auth/email-already-in-use') {
            
            dispatch(setAlert("Email has already been taken.", 'danger'));

        } else if(err.code == 'auth/weak-password') {

            dispatch(setAlert("Password too short.", 'danger'));

        } else if(err.code == 'auth/invalid-email') {

            dispatch(setAlert("Invalid email address.", 'danger'));
        } else {
            dispatch(setAlert("Something went wrong. Try again.", 'danger'));
        }

        dispatch(setAccountLoadingDone());

        dispatch({
            type: REGISTER_FAIL
        });
    }
}

// Login User
export const loginWithEmailAndPassword = (
    email, 
    password
) => async dispatch => {

    dispatch(setAccountLoading());

    try {
        
        const userCredential = await signInWithEmailAndPassword(auth, email, password);

        // Store the user ID in local storage
        localStorage.setItem("user_id", userCredential.user.uid);

        // Load the user data into the Redux store (register = false, login = true)
        dispatch(loadUser(false, true));
        
    } catch (err) {
        
        // --- Handle different error codes and dispatch corresponding actions or alerts
        if(err.code == 'auth/too-many-requests') {
            
            dispatch(setAlert("Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later.", 'danger', 7800));

        } else  if(err.code == 'auth/wrong-password') {

            dispatch(setAlert("Wrong password!", 'danger'));

        } else if(err.code == 'auth/user-not-found') {

            dispatch(setAlert("Incorrect. Please try again.", 'danger'));

        } else if(err.code == 'auth/internal-error') {
            
            dispatch(setAlert("Internal server error.", 'danger'));

        } else if(err.code == 'auth/invalid-email') {

            dispatch(setAlert("Invalid email address.", 'danger'));

        } else if(err.code == 'auth/invalid-login-credentials') {

            dispatch(setAlert("Invalid login credentials.", 'danger'));

        } else {
            
            dispatch(setAlert("Something went wrong. Try again.", 'danger'));
        }

        dispatch({
            type: LOGIN_FAIL
        });
    } finally {
        dispatch(setAccountLoadingDone());
    }
}

// Handle Continue with Google
export const signInWithGoogle = (
    userCredential, 
    {
        email
    }
) => async dispatch => {

    console.log('SIGNING IN WITH GOOGLE')
    dispatch(setAccountLoading());

    try {

        // --- Check if User exists

        // Create a query to check if the username already exists in the users collection
        const userEmailQuery = query (usersCollectionRef, where("email", "==", email))

        // Retrieve the list of users with the same email
        let usersArray = await getDocs(userEmailQuery);

        let user_List = usersArray.docs.map((doc) => ( {...doc.data(), _id: doc.id} ))

        if(user_List.length == 0) {
            // Sign up user

            // Create a new document in the "users" collection with the user data
            handle_create_user(
                userCredential, 
                {
                    email,
                    verified: true
                }
            )

        } else {
            // Login user

            // Store the user ID in local storage
            localStorage.setItem("user_id", user_List[0]._id);

            dispatch(loadUser(false, true));

        }
        
    } catch (err) {
        console.log('ERROR SIGNING IN WITH GOOGLE')
        console.log(err);

        dispatch(setAlert("Error Signing In with Google.", 'danger'));
        
        dispatch(setAccountLoadingDone());
    }
}

// Forgot Password - send link
export const passwordReset = async (email) => {

    console.log('SENDING EMAIL')
    return await sendPasswordResetEmail(auth, email)
}

// Forgot Password - reset password
export const confirmThePasswordReset = async (oobCode, newPassword) => {
    
    if(!oobCode || !newPassword) return;
    
    return await confirmPasswordReset(auth, oobCode, newPassword)
}


// Logout
export const logout = () => async dispatch => {
    try {

        // Sign out the user with firebase hook
        // await signOut(auth);

        // Dispatch the LOGOUT action to clear the user data from the Redux store
        dispatch({type: LOGOUT})
    } catch (err) {
        console.log(err);
    }
}

// Update auth inputs
export const changePassword = (formData) => async dispatch => {
    const { password } = formData;
    console.log('UPDATING PASSWORD');

    // Try block to handle the update operation
    try {

        // Update the user's password
        await updatePassword(auth.currentUser, password);

        console.log('UPDATE PASSWORD SUCCESS');

        // dispatch({
        //     type: UPDATE_AUTH,
        //     payload: res.data
        // });

        // Dispatch a success alert indicating that the password has been updated
        dispatch(setAlert("Password Updated", 'success'));

    } catch (err) {
        console.log('ERRORS')
        console.log(err);
        
        if(err.response) {
            const errors = err.response.data.errors;

            // Dispatch alerts for each error received from the server
            if(errors) {
                errors.forEach(error => dispatch(setAlert(error.msg, 'danger')));
            }
        } else {

            // Display a generic error message if no specific error response is received
            dispatch(setAlert('Something went wrong', 'danger'));
        }
    }
}

// Update auth inputs
export const changeUserEmail = (formData) => async dispatch => {
    const { email, userId } = formData;

    // Reference to the user document in the Firestore database
    const userDoc = doc(db, "users", userId);

    // Try block to handle the update operation
    try {

        
        // Update the user's email in the authentication system
        await updateEmail(auth.currentUser, email);
        
        // Update the user's email in the Firestore database
        await updateDoc(userDoc, {
            email: email
        })

        console.log('UPDATE EMAIL SUCCESS');

        // Get updated User data for display
        const updatedUserDoc = await getDoc(userDoc);

        console.log('GOT UPDATED USER BY ID');
  
        // Dispatch an action to update the authenticated user's information with the updated data
        dispatch({
            type: UPDATE_AUTH,
            payload: updatedUserDoc.data()
        });

        // dispatch({
        //     type: UPDATE_AUTH,
        //     payload: res.data
        // });

        // Dispatch a success alert indicating that the profile has been updated
        dispatch(setAlert('Email Updated', 'success'));

    } catch (err) {
        console.log('ERRORS')
        console.log(err);
        
        // Display a generic error message if no specific error response is received
        dispatch(setAlert('Something went wrong', 'danger'));
    }
}

// Remove current user info from state
export const clearUser = () => dispatch => {
    dispatch({
        type: CLEAR_USER
    });

}

// Account creation loading
export const setAccountLoading = () => {
    return {
        type: ACCOUNT_LOADING
    }
}

// Account creation done
export const setAccountLoadingDone = () => {
    return {
        type: ACCOUNT_LOADING_DONE
    }
}

// -- Subscription Logic --

// Set state if user is subscribed to premium
export const setIsPremium = (isPremium) => dispatch => {
    dispatch({
        type: SET_STATUS, /** Used this name as a security measure, in case anyone can see redux events. */
        payload: isPremium
    });
}

// -- User Account Configurations --

// Update User's default programming language 
export const updateLanguage = (language) => async dispatch => {

    if(auth.currentUser == null) return;

    // Reference to the user document in the Firestore database
    const userDoc = doc(db, "users", auth.currentUser.uid);

    console.log("UPDATING USER's LANGUAGE");

    // Try block to handle the update operation
    try {

        // Update the user's birth date info
        await updateDoc(userDoc, {
            default_language: language,
        })

        // Get updated User data for display
        const updatedUserDoc = await getDoc(userDoc);

        console.log('GOT UPDATED USER BY ID');
  
        // Dispatch an action to update the authenticated user's information with the updated data
        dispatch({
            type: UPDATE_AUTH,
            payload: updatedUserDoc.data()
        });

        console.log('USER UPDATED');

    } catch (err) {
        console.log('ERRORS')
        console.log(err);
    }
}

// Update User's default problem style
export const updateProblemStyle = (problem_style) => async dispatch => {

    if(auth.currentUser == null) return;

    // Reference to the user document in the Firestore database
    const userDoc = doc(db, "users", auth.currentUser.uid);

    console.log("UPDATING USER's PROBLEM STYLE");

    // Try block to handle the update operation
    try {

        // Update the user's birth date info
        await updateDoc(userDoc, {
            problem_style,
        })

        // Get updated User data for display
        const updatedUserDoc = await getDoc(userDoc);

        console.log('GOT UPDATED USER BY ID');
  
        // Dispatch an action to update the authenticated user's information with the updated data
        dispatch({
            type: UPDATE_AUTH,
            payload: updatedUserDoc.data()
        });

        console.log('USER UPDATED');

    } catch (err) {
        console.log('ERRORS')
        console.log(err);
    }
}


